import React from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CButton } from '@coreui/react-pro'

import { api, ApiError } from '../api/client'
import { AuthContext } from '../App'
import EntityPage, { FieldSpec } from '../components/EntityPage'
import Linkify from '../components/Linkify'
import { canModifyAny } from '../auth/capabilities'
import { Person, usePeople, usePersonLabels, joinLabels } from '../api/people'

// A non-empty links field is what makes an idea "concrete" — the two sections
// below are the same table, split on it, so adding a link promotes a row.
const hasLinks = (r: any) => !!(r.links ?? '').trim()

function selfPersonId(people: Person[] | undefined, email: string | undefined): string {
  if (!email) return ''
  const match = (people ?? []).find(
    (p) => (p.email ?? '').trim().toLowerCase() === email.trim().toLowerCase(),
  )
  return match ? String(match.id) : ''
}

/** Heart button + voter names. One tap toggles your own interest — this is
 *  the one write any signed-in user (viewers included) can perform. */
const HeartCell: React.FC<{ idea: any }> = ({ idea }) => {
  const { auth } = React.useContext(AuthContext)
  const queryClient = useQueryClient()
  const people = usePeople()
  const labels = usePersonLabels()

  const myId = selfPersonId(people.data, auth.user?.email)
  const ids: number[] = idea.interested_ids ?? []
  const mine = !!myId && ids.includes(Number(myId))

  const toggle = useMutation({
    mutationFn: () => api.post(`/v1/ideas/${idea.id}/interest`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ideas'] }),
    onError: (err) => {
      window.alert(err instanceof ApiError ? err.message : "Couldn't save that — try again.")
    },
  })

  return (
    <span className="d-inline-flex align-items-center gap-2 flex-wrap">
      <CButton
        size="sm" variant="outline"
        color={mine ? 'danger' : 'secondary'}
        disabled={toggle.isPending}
        aria-label={mine ? 'Remove my interest' : "I'm interested"}
        onClick={() => toggle.mutate()}
      >
        {mine ? '❤️' : '🤍'} {ids.length}
      </CButton>
      <span className="small text-body-secondary">
        {joinLabels(labels, ids) || '—'}
      </span>
    </span>
  )
}

// Editors/admins keep the full form: they can attribute ideas to anyone and
// mark interest for people who never sign in. Contributors get Suggested by
// locked to themselves (hearts are how they express interest).
const ideaFields = (linksRequired: boolean, trusted: boolean): FieldSpec[] => {
  const fields: FieldSpec[] = [
    { name: 'idea', label: 'Idea', type: 'text', required: true },
    { name: 'suggested_by_id', label: 'Suggested by', type: 'person', disabled: !trusted },
  ]
  if (trusted) {
    fields.push({ name: 'interested_ids', label: "Who's interested", type: 'people' })
  }
  fields.push(
    {
      name: 'links', label: 'Links (one URL per line)', type: 'textarea', required: linksRequired,
      placeholder: 'https://…\nhttps://… (a second option, if comparing)',
    },
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'where, when, cost guesses' },
  )
  return fields
}

const renderLinks = (r: any) => (
  <>
    {(r.links ?? '').split('\n').filter(Boolean).map((u: string, i: number) => (
      <div key={i} className="text-break">
        <a href={u} target="_blank" rel="noopener noreferrer">{u}</a>
      </div>
    ))}
  </>
)

// Most loved first, then oldest first.
const byHearts = (a: any, b: any) =>
  ((b.interested_ids ?? []).length - (a.interested_ids ?? []).length) || (a.id - b.id)

const Ideas: React.FC = () => {
  const { auth } = React.useContext(AuthContext)
  const people = usePeople()
  const trusted = canModifyAny(auth.user?.role)

  // Pre-fills the Add form only; editing keeps whatever the row already has.
  const blank = { suggested_by_id: selfPersonId(people.data, auth.user?.email) }

  const columns = (withLinks: boolean) => {
    const cols = [
      {
        key: 'idea',
        label: 'Idea',
        width: '200px',
        render: (r: any) => (
          <Link to={`/ideas/${r.id}/discussion`} className="text-decoration-none">
            {r.idea}
          </Link>
        ),
      },
      {
        key: 'interested_ids',
        label: 'Interest',
        width: '180px',
        render: (r: any) => <HeartCell idea={r} />,
      },
      {
        key: 'discussion',
        label: '💬',
        width: '80px',
        render: (r: any) => (
          <Link to={`/ideas/${r.id}/discussion`} className="text-decoration-none">
            <CButton size="sm" color="secondary" variant="outline">
              💬 {r.comment_count}
            </CButton>
          </Link>
        ),
      },
      {
        // no width — with fixedLayout this column absorbs the leftover space
        // (EntityPage floors it at 240px via the table min-width)
        key: 'discussion_summary',
        label: 'Discussion Summary',
        render: (r: any) => (
          <span className="small text-body-secondary">
            {r.discussion_summary
              || (r.comment_count > 0 ? 'Generating summary…' : 'No discussion yet.')}
          </span>
        ),
      },
    ]
    if (withLinks) {
      cols.push({
        key: 'links',
        label: 'Links',
        width: '220px',
        render: renderLinks,
      } as any)
    }
    cols.push({
      key: 'notes',
      label: 'Notes',
      width: '220px',
      render: (r: any) => <Linkify text={r.notes} />,
    } as any)
    return cols
  }

  return (
    <>
      <EntityPage
        entity="ideas"
        title="💡 Concrete ideas"
        description="Flushed out, with a booking link — one step from the Booked Activities page."
        addLabel="Add concrete idea"
        emptyHint="Nothing concrete yet — add an idea with a link."
        filter={hasLinks}
        sortBy={byHearts}
        fixedLayout
        columns={columns(true)}
        fields={ideaFields(true, trusted)}
        blank={blank}
      />
      <EntityPage
        entity="ideas"
        title="💭 Vague Ideas"
        description={<>The parking lot — anything goes. Once an idea includes <b>a link</b> it automatically gets moved up to Concrete.</>}
        addLabel="Add other idea"
        filter={(r) => !hasLinks(r)}
        sortBy={byHearts}
        fixedLayout
        columns={columns(false)}
        fields={ideaFields(false, trusted)}
        blank={blank}
      />
    </>
  )
}

export default Ideas
